// @dave/level-contract — ЕДИНЫЙ источник правды между метой и плеерами уровней
// (architecture.md §3, client-detailed-design.md §2). Только типы + isCompatible.
// Эволюция: minor — только опциональные поля; major — синхронный апдейт всех плееров.

export const CONTRACT_VERSION = '1.4.0';
export type LevelMode = 'play' | 'replay' | 'edit';

export interface ThemeTokens {
  // палитра бренда: конкретные значения задаёт мета из theme-модуля;
  // плеер не хардкодит цвета — только токены
  bg: string; ink: string;
  accent: string;        // teal — «починено/правильно»
  accent2: string;       // ржавый/оранж — «сломано/багнар»
  surface: string; overlay: string;
  fontMono: string; fontSans: string;
}

export interface LevelConfig {
  levelId: string; playerType: string;
  data: unknown;                 // ОПАК для меты
  mode: LevelMode;
  locale: string;
  settings: { music: number; sfx: number; reducedMotion: boolean; input: string };
  theme: ThemeTokens;
  seed: number;                  // ОБЯЗАТЕЛЕН (детерминизм); источник — бэкенд (levels.seed)
  /** 1.2 (опционально): базовый URL медиа плеера на S3/CDN — выдаёт сервис
   *  ассетов (GET /api/v1/assets, players[].baseUrl по playerType); id в data
   *  уровня (bg, tools[].type…) резолвит в файлы сам плеер. */
  /** 1.3 (опционально): `cacheBust` — токен антикэша, который плеер клеит ко всем
   *  своим медиа-URL (`?v=<token>`). Ставит его мета и только в стадии проекта
   *  development (web/src/config/stage.ts): перезалитый на S3 арт виден сразу.
   *  Пусто (пред-релиз/операционка) — URL чистые, кэш работает штатно. */
  media?: { baseUrl: string; cacheBust?: string };
  /** 1.4 (опционально): ВЫБОР ГАРДЕРОБА — что надето на жителе. Свойство ИГРОКА, а не
   *  уровня: и скин, и шапка открываются за собранные в мире звёзды и несут перки, мета
   *  берёт выбор из прогресса и кладёт сюда.
   *
   *  `skin` — id облика жителя; пусто → житель МИРА УРОВНЯ (дефолт), то есть в мире 5
   *  бежит житель мира 5. Выбранный в гардеробе скин этот дефолт перебивает.
   *  `hat` — id шапки; пусто → без шапки.
   *  Плеер знает про id только две вещи: где взять арт и как он сидит (render/hats.ts). */
  skin?: string;
  hat?: string;
  replay?: ReplayData;
  resumeToken?: unknown; capabilities?: string[];
}

export interface LevelResult {
  levelId: string; status: 'completed' | 'failed' | 'quit' | 'error';
  rating: number; maxRating: number;
  objectives: { id: string; done: boolean }[];
  metrics: Record<string, number | string>;
  events: { type: string; t: number; payload?: unknown }[];  // t = кадр симуляции
  replay?: ReplayData;
  durationMs: number; resumeToken?: unknown;
}

export interface ReplayData {
  contractVersion: string; playerType: string; levelId: string;
  seed: number; configHash: string;
  inputs: { f: number; type: string; payload?: unknown }[];
  frames: number; fps: number;
}

export type CreateLevel  = (mount: HTMLElement, config: LevelConfig, host: LevelHost)  => LevelInstance;
export type CreateEditor = (mount: HTMLElement, data: unknown | null, host: EditorHost) => EditorInstance;

export interface LevelInstance {
  start(): void; pause(): void; resume(): void;
  setMuted(m: boolean): void; setViewport(w: number, h: number): void; destroy(): void;
  /** 1.1 (опционально): живое применение громкостей 0..1 из настроек меты
   *  без пересоздания уровня; стартовые значения — LevelConfig.settings. */
  setVolumes?(music: number, sfx: number): void;
  getReplay(): ReplayData;
  renderReplay(replay: ReplayData, opts: { fps: number; width: number; height: number },
               onFrame: (canvas: HTMLCanvasElement, frame: number) => Promise<void>): Promise<void>;
  requestHint(): { toolType: string; at: unknown } | null;   // v1: все плееры возвращают null
}

export interface EditorInstance {                            // фаза 2 (UGC)
  getData(): unknown;
  validate(): Promise<{ solvable: boolean; trivial: boolean; notes?: string }>;
  getThumbnail(): Promise<Blob>;
  destroy(): void;
}

export interface LevelHost {
  onReady(): void; onProgress(p: number): void;
  onComplete(r: LevelResult): void; onError(e: { code: string; message: string }): void;
  onAnalytics(ev: { name: string; props?: object }): void; onRequestQuit(): void;
}
export interface EditorHost { onSave(data: unknown, thumb: Blob): void; onTest(data: unknown): void; onQuit(): void; }

/** semver-major совместимость: '1.x' плеер ↔ '1.y' host. */
export function isCompatible(hostVersion: string, playerVersion: string): boolean {
  return hostVersion.split('.')[0] === playerVersion.split('.')[0];
}
